import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@veil/ui/components/button";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "@veil/ui/components/card";
import { Skeleton } from "@veil/ui/components/skeleton";
import { formatRelative } from "@veil/ui/lib/format";
import { ArrowLeft } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "wouter";
import { OperationLogViewer } from "@/components/operation-log-viewer";
import {
	OperationStepTimeline,
	type TimelineStep,
} from "@/components/operation-step-timeline";
import { StatusBadge } from "@/components/status-badge";
import { api, unwrap } from "@/lib/api";
import { type LiveState, useOperationLive } from "@/lib/use-operation-live";

interface OperationDetailData {
	success: boolean;
	data: {
		id: string;
		kind: string;
		status: string;
		inputs: Record<string, unknown> | string;
		startedAt: string;
		finishedAt: string | null;
		exitCode: number | null;
		logFilePath: string | null;
		logTail: string | null;
		progress: TimelineStep[] | null;
		user: { username: string; role: string } | null;
	};
}

export function OperationDetail({ id }: { id: string }) {
	// One-shot metadata fetch. No `refetchInterval` — the SSE stream owns
	// every mutable field (status, progress, log) once the page mounts,
	// and a poll on top would just dual-write the same UI state.
	const { data, isLoading } = useQuery({
		queryKey: ["operation", id],
		queryFn: () =>
			unwrap(api.api.operations({ id }).get()) as Promise<OperationDetailData>,
	});

	const op = data?.data;

	if (isLoading) return <Skeleton className="h-64" />;
	if (!op)
		return <div className="text-sm text-muted-foreground">Not found.</div>;

	return <OperationDetailLoaded op={op} />;
}

/**
 * Split out so `useOperationLive` only mounts once `op` is non-null —
 * otherwise we'd need a placeholder seed and an `enabled` flag that
 * tears the subscription down and back up on the first paint.
 */
function OperationDetailLoaded({ op }: { op: OperationDetailData["data"] }) {
	const seed: LiveState = {
		status: op.status,
		exitCode: op.exitCode,
		finishedAt: op.finishedAt,
		steps: op.progress ?? [],
		log: stripStepMarkers(op.logTail ?? ""),
		streamClosed: false,
	};

	const queryClient = useQueryClient();
	// When a fleet-* operation's stream ends (terminal status reached), the
	// backend has already written a fresh snapshot for the host. Invalidate
	// the fleet caches so the workers overview and the host page (on return)
	// show the landed version instead of stale data. Done from the stream's
	// terminal-event handler, not a status-watching effect.
	const onStreamEnd = useCallback(() => {
		if (op.kind.startsWith("fleet-")) {
			queryClient.invalidateQueries({ queryKey: ["workers"] });
		}
	}, [op.kind, queryClient]);

	const live = useOperationLive(op.id, seed, { onEnd: onStreamEnd });

	// Very-old terminal ops have their full log on disk (logFilePath) but
	// no longer in `logTail` (truncated to 10KB) and the SSE handler
	// hands us only the post-snapshot tail plus the closing events. Fetch
	// the full file once the stream has closed so the operator can see
	// the whole thing instead of just the last 10KB.
	const [fullLogLoaded, setFullLogLoaded] = useState(false);
	const [fullLog, setFullLog] = useState<string | null>(null);
	useEffect(() => {
		if (fullLogLoaded) return;
		if (op.status === "running") return;
		if (!live.data.streamClosed) return;
		if (live.data.log.length > 0) return;
		if (!op.logFilePath) return;
		let cancelled = false;
		(async () => {
			try {
				const res = await fetch(`/api/operations/${op.id}/log`, {
					credentials: "include",
				});
				const body = (await res.json()) as { log?: string };
				if (!cancelled) {
					setFullLog(stripStepMarkers(body.log ?? ""));
					setFullLogLoaded(true);
				}
			} catch {
				// Best-effort recovery — log card simply stays empty if
				// the file is gone, which matches the previous behavior.
				if (!cancelled) setFullLogLoaded(true);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [
		fullLogLoaded,
		op.id,
		op.logFilePath,
		op.status,
		live.data.streamClosed,
		live.data.log.length,
	]);

	const [cancelInFlight, setCancelInFlight] = useState(false);
	const onCancel = async () => {
		if (cancelInFlight) return;
		setCancelInFlight(true);
		try {
			await live.cancel();
			// Intentionally no UI flip here — the backend acknowledges
			// the request and the SSE `status` event will move the page
			// into the failed state once the pipeline notices the flag.
		} catch (err) {
			toast.error(
				err instanceof Error ? err.message : "Failed to cancel operation",
			);
		} finally {
			setCancelInFlight(false);
		}
	};

	const status = live.data.status;
	const exitCode = live.data.exitCode;
	const finishedAt = live.data.finishedAt;
	const steps = live.data.steps;
	const logText = fullLog ?? live.data.log;

	return (
		<div className="space-y-6">
			<div className="flex items-center gap-3">
				<Button variant="ghost" size="icon" asChild>
					<Link href="/operations">
						<ArrowLeft className="h-4 w-4" />
					</Link>
				</Button>
				<div>
					<h1 className="text-2xl font-semibold tracking-tight">{op.kind}</h1>
					<p className="text-sm text-muted-foreground font-mono">{op.id}</p>
				</div>
				<div className="ml-auto flex items-center gap-2">
					{status === "running" && (
						<Button
							variant="destructive"
							size="sm"
							onClick={onCancel}
							disabled={cancelInFlight}
						>
							{cancelInFlight ? "Cancelling…" : "Cancel"}
						</Button>
					)}
					<StatusBadge status={status} />
				</div>
			</div>

			<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
				<Card>
					<CardHeader>
						<CardTitle className="text-base">Metadata</CardTitle>
					</CardHeader>
					<CardContent className="space-y-2 text-sm">
						<KV label="Started" value={formatRelative(op.startedAt)} />
						<KV label="Finished" value={formatRelative(finishedAt)} />
						<KV
							label="Exit code"
							value={exitCode === null ? "—" : String(exitCode)}
						/>
						<KV label="By" value={op.user?.username ?? "—"} />
						<KV label="Log file" value={op.logFilePath ?? "—"} />
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle className="text-base">Inputs</CardTitle>
					</CardHeader>
					<CardContent>
						<pre className="overflow-auto rounded-md bg-muted p-3 text-xs">
							{JSON.stringify(op.inputs, null, 2)}
						</pre>
					</CardContent>
				</Card>
			</div>

			{/* Timeline only renders for in-process pipelines that emit
			    structured progress. Shell-out operations still fall
			    through to the raw log viewer below. */}
			{steps.length > 0 && (
				<OperationStepTimeline steps={steps} operationStatus={status} />
			)}

			<OperationLogViewer text={logText} status={status} />
		</div>
	);
}

function KV({ label, value }: { label: string; value: string }) {
	return (
		<div className="flex items-center justify-between gap-3">
			<span className="text-muted-foreground">{label}</span>
			<span className="font-mono text-xs">{value}</span>
		</div>
	);
}

/**
 * The backend prefixes structured step events with a sentinel so they
 * can be split into a separate SSE channel. The /log endpoint (terminal
 * operations only) and the cached `logTail` may still include those
 * marker lines. Strip them here so the log card never shows the
 * machine-readable JSON to the operator.
 */
const STEP_LINE_PREFIX = "##VEILSTEP## ";
function stripStepMarkers(text: string): string {
	if (!text.includes(STEP_LINE_PREFIX)) return text;
	return text
		.split("\n")
		.filter((line) => !line.startsWith(STEP_LINE_PREFIX))
		.join("\n");
}
